<template>
  <v-navigation-drawer
    v-model="devShowSettings"
    class="dev-settings-drawer"
    location="right"
    temporary
    touchless
    :width="drawerWidth"
  >
    <v-toolbar flat height="52">
      <v-app-bar-nav-icon v-if="smAndUp">
        <v-btn
          :icon="isDefaultWidth ? 'far fa-angles-left' : 'far fa-angles-right'"
          @click="onToggleWidth"
        />
      </v-app-bar-nav-icon>
      <v-spacer v-if="!isDefaultWidth" />
      <v-toolbar-title>
        <span class="text-title-large">DeepSign DEV Settings</span>
      </v-toolbar-title>
      <v-spacer v-if="!isDefaultWidth" />
      <v-btn icon="$close" @click="devShowSettings = false" />
    </v-toolbar>

    <v-card-text>
      <v-switch
        v-model="settingsDevStore.devModeEnabled"
        hide-details
        inset
        label="DevMode Enabled"
      />
      <span class="text-body-small">Enable or disable the dev mode</span>

      <v-divider class="ma-2" />

      <v-form :disabled="!settingsDevStore.devModeEnabled">
        <div v-for="feature in featuresItems" :key="feature.id">
          <v-switch
            v-if="feature.type === 'toggle'"
            v-model="settingsDevStore.devModeFeatures"
            hide-details
            :label="feature.text"
            multiple
            :value="feature.value"
          >
            <template #label>
              <div class="flex-grow-1">
                {{ feature.text }}
              </div>

              <DeepStageLabel
                v-if="feature.flag"
                class="ml-1"
                :flag="feature.flag"
                icon-display-as-fallback="chip"
                size="x-small"
              />
            </template>
          </v-switch>
          <v-btn
            v-else
            block
            class="mt-4"
            :color="feature.color"
            :disabled="!settingsDevStore.devModeEnabled"
            slim
            variant="outlined"
            @click="feature.action"
          >
            {{ feature.text }}
            <DeepStageLabel
              v-if="feature.flag"
              class="ms-2"
              :flag="feature.flag"
              icon-display-as-fallback="chip"
              size="x-small"
            />
          </v-btn>
          <span
            v-if="feature.description"
            class="text-body-small"
            :style="{
              marginTop: feature.type === 'toggle' ? '-6px' : '4px',
              display: 'block'
            }"
          >
            {{ feature.description }}
          </span>
          <v-form
            v-if="
              feature.type === 'toggle' &&
              feature.settings &&
              feature.settings.length > 0
            "
            class="pl-8"
            :disabled="
              !settingsDevStore.devModeFeatures.includes(feature.value) ||
              !settingsDevStore.devModeEnabled
            "
          >
            <template
              v-for="featureSetting in feature.settings"
              :key="featureSetting.id"
            >
              <v-checkbox
                color="primary"
                density="compact"
                hide-details
                :label="featureSetting.text"
                :model-value="featureSetting.value"
                @update:model-value="featureSetting.click"
              />
              <span
                v-if="featureSetting.description"
                class="text-body-small"
                style="margin-top: -6px; display: block"
              >
                {{ featureSetting.description }}
              </span>
            </template>
          </v-form>
        </div>
      </v-form>

      <v-divider class="ma-4" />

      <div v-if="isLocalEnv()">
        <v-switch
          false-icon="far fa-sun-bright"
          hide-details
          inset
          :label="`Theme: ${isDarkTheme ? 'dark' : 'light'}`"
          :model-value="isDarkTheme"
          true-icon="far fa-moon"
          @update:model-value="toggleLightDarkTheme()"
        />
        <span class="text-body-small">Switch theme to dark/light</span>
      </div>
    </v-card-text>

    <template #append>
      <v-card-text>
        <v-divider />
        <div
          v-for="releaseInfo in releaseInfos"
          :key="releaseInfo.title"
          class="text-medium-emphasis text-body-small py-2 px-3 d-flex align-center"
        >
          <div class="d-inline-flex align-center">
            <v-icon size="small" start>{{ releaseInfo.icon }}</v-icon>
            {{ releaseInfo.title }}
          </div>
          <span class="ms-auto text-truncate">{{ releaseInfo.value }}</span>
          <DeepBtnCopyToClipboard
            v-if="releaseInfo.valueToCopy"
            class="ms-1"
            size="small"
            :text="releaseInfo.valueToCopy"
          />
        </div>
      </v-card-text>
    </template>
  </v-navigation-drawer>

  <ConfirmationDialog ref="confirmationDialog" />
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import {
  DeepBtnCopyToClipboard,
  DeepStageLabel,
  type Flags,
  useAppTheme
} from '@deepcloud/deep-ui-lib';
import { formatDateTimeByDefaultFormat } from '@deepcloud/deep-ui-utils/date';
import {
  DEV_MODE_FEATURES,
  type DevModeFeatures,
  useSettingsDevStore
} from '@/stores/settingsDev.ts';
import { DeviceKey } from '@/plugins/device-detector-js.ts';
import AuthService from '@/services/authService.ts';
import { useLocalPreferencesStore } from '@/stores/localPreferences.ts';
import { useMessageStore } from '@/stores/message.ts';
import { isLocalEnv, isLocalOrDevEnv } from '@/utils/env.ts';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';

const { isDarkTheme, toggleLightDarkTheme } = useAppTheme();
const { xs, smAndUp } = useDisplay();

const settingsDevStore = useSettingsDevStore();
const localPreferencesStore = useLocalPreferencesStore();
const messageStore = useMessageStore();
const { devShowSettings } = storeToRefs(settingsDevStore);
const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog>>();

// Always close the drawer on load — a persisted "open" state should never
// auto-open the drawer on page load.
devShowSettings.value = false;

const DRAWER_DEFAULT_WIDTH = 400;
const drawerWidth = ref(DRAWER_DEFAULT_WIDTH);

const isDefaultWidth = computed(
  () => drawerWidth.value === DRAWER_DEFAULT_WIDTH
);

type FeatureSetting = {
  id: string;
  text: string;
  value: boolean;
  description?: string;
  click: (value: boolean | null) => void;
};

type ToggleFeatureItem = {
  id: string;
  type: 'toggle';
  text: string;
  value: DevModeFeatures;
  flag?: Flags;
  description?: string;
  settings?: FeatureSetting[];
  show?: () => boolean;
};

type ButtonFeatureItem = {
  id: string;
  type: 'button';
  text: string;
  flag?: Flags;
  description?: string;
  color?: string;
  action: () => void;
  show?: () => boolean;
};

type FeatureItem = ToggleFeatureItem | ButtonFeatureItem;

const featuresItems = computed<FeatureItem[]>(() =>
  (
    [
      {
        id: DEV_MODE_FEATURES[0],
        type: 'button',
        text: 'Clear document wizards',
        flag: 'DEV' as Flags,
        color: 'warning',
        description:
          'Removes all pending document IDs that trigger the setup wizard on drafts',
        action: confirmClearDocumentWizards,
        show: () => Boolean(AuthService.isAuthenticated && isLocalOrDevEnv())
      },
      /* {
        id: DEV_MODE_FEATURES[0],
        type: 'toggle',
        text: 'DeepSign Reset Stepper Wizard',
        value: DEV_MODE_FEATURES[0] as DevModeFeatures,
        flag: 'DEV' as Flags,
        description:
          'Resets the DeepSign Wizard entries on the user profile and allows the wizard to appear initially on first document opening',
        settings: [
          {
          id: 'test-1',
          text: 'API Task polling',
          value: {},
          description: 'Enables active tasks polling from the API',
          click: () => {}
        }
        ]
      },*/
      {
        id: DEV_MODE_FEATURES[1],
        type: 'toggle',
        text: 'DeepSign sign8',
        value: DEV_MODE_FEATURES[1] as DevModeFeatures,
        flag: 'DEV' as Flags,
        description: 'Enables deepSign sign8 identification method',
        show: () => true
      },
      {
        id: DEV_MODE_FEATURES[2],
        type: 'toggle',
        text: 'DeepA Summarize',
        value: DEV_MODE_FEATURES[2] as DevModeFeatures,
        flag: 'DEV' as Flags,
        description: 'Enables deepA document summarize feature',
        show: () => true
      }
    ] satisfies FeatureItem[]
  ).filter((feature) => (feature.show ? feature.show() : true))
);

function resetDrawerWidth() {
  drawerWidth.value = DRAWER_DEFAULT_WIDTH;
}

function onToggleWidth() {
  if (isDefaultWidth.value) {
    drawerWidth.value = window.innerWidth;
  } else {
    resetDrawerWidth();
  }
}

function confirmClearDocumentWizards() {
  confirmationDialog.value?.open(
    'Clear document wizards (DEV)',
    'This will remove all pending document ids that trigger the setup wizard on drafts',
    clearDocumentWizards
  );
}

function clearDocumentWizards() {
  localPreferencesStore.documentsWizards = [];
  messageStore.showMessage({
    text: 'Cleared documentsWizards preference',
    color: 'success',
    icon: 'fa fa-circle-check'
  });
}

watch(
  () => devShowSettings.value,
  (newValue) => {
    if (newValue && xs.value) {
      drawerWidth.value = window.innerWidth;
    } else {
      resetDrawerWidth();
    }
  }
);

const releaseInfos = computed(() => [
  {
    title: 'Environment',
    icon: 'far fa-code',
    value: import.meta.env.VITE_ENVIRONMENT
  },
  {
    title: 'Tag',
    icon: 'far fa-tag',
    value: import.meta.env.VITE_RELEASE_TAG
  },
  {
    title: 'Datetime',
    icon: 'far fa-clock',
    value: import.meta.env.VITE_RELEASE_DATETIME
      ? formatDateTimeByDefaultFormat(import.meta.env.VITE_RELEASE_DATETIME)
      : ''
  },
  {
    title: 'Commit ID',
    icon: 'far fa-code-commit',
    value: import.meta.env.VITE_RELEASE_COMMIT_ID
  },
  {
    title: 'Auth Token',
    icon: 'far fa-key',
    value: AuthService.accessToken
      ? `${AuthService.accessToken.substring(0, 10)}...`
      : '',
    valueToCopy: AuthService.accessToken
  }
]);

const device = inject(DeviceKey);

function onKeyDown(e: KeyboardEvent) {
  const keyCommandMatch = device?.isMacOS
    ? e.metaKey && e.key === 'd'
    : e.ctrlKey && e.key === 'd';
  if (keyCommandMatch) {
    devShowSettings.value = !devShowSettings.value;
    e.preventDefault();
  }
}

onMounted(() => window.addEventListener('keydown', onKeyDown));
onUnmounted(() => window.removeEventListener('keydown', onKeyDown));
</script>

<style lang="scss" scoped>
.dev-settings-drawer {
  // Show the drawer above dialogs. 2500 is Vuetify's default VOverlay
  // z-index, so this needs to be one above that.
  z-index: 2501 !important;
}
</style>
